/**
 * LaTeX language definition for Monaco Editor.
 *
 * Provides:
 *  - Tokenizer for syntax highlighting (commands, environments, math, comments)
 *  - Language configuration (brackets, auto-closing, comments)
 *  - A custom light theme ("latex-light") matching the Figma design
 */

import type { languages, editor } from "monaco-editor"
import { registerLatexCompletionProvider } from "./monaco-latex-completions"
import { registerLatexFoldingProvider } from "./monaco-latex-folding"

// Re-export per-editor attach functions so consumers can import from one place
export { attachBeginEndMatching } from "./monaco-latex-matching"
export { attachDiagnostics } from "./monaco-latex-diagnostics"

/* ── Language ID ── */
export const LATEX_LANGUAGE_ID = "latex"

/* ── Language Configuration ── */
export const latexLanguageConfiguration: languages.LanguageConfiguration = {
  comments: {
    lineComment: "%",
  },
  brackets: [
    ["{", "}"],
    ["[", "]"],
    ["(", ")"],
  ],
  autoClosingPairs: [
    { open: "{", close: "}" },
    { open: "[", close: "]" },
    { open: "(", close: ")" },
    { open: "$", close: "$" },
  ],
  surroundingPairs: [
    { open: "{", close: "}" },
    { open: "[", close: "]" },
    { open: "(", close: ")" },
    { open: "$", close: "$" },
    { open: "`", close: "'" },
  ],
  folding: {
    markers: {
      start: /\\begin\{/,
      end: /\\end\{/,
    },
  },
  indentationRules: {
    increaseIndentPattern: /\\begin\{(?!document)/,
    decreaseIndentPattern: /\\end\{(?!document)/,
  },
}

/* ── Monarch Tokenizer ── */
export const latexMonarchLanguage: languages.IMonarchLanguage = {
  defaultToken: "text",
  tokenPostfix: ".tex",

  // keywords (environments etc.)
  keywords: [
    "document",
    "equation",
    "align",
    "figure",
    "table",
    "itemize",
    "enumerate",
    "tabular",
    "array",
    "abstract",
    "center",
    "flushleft",
    "flushright",
    "minipage",
    "verbatim",
    "lstlisting",
    "tikzpicture",
    "frame",
    "theorem",
    "proof",
    "definition",
    "lemma",
    "corollary",
    "proposition",
    "remark",
    "example",
  ],

  tokenizer: {
    root: [
      // Comments
      [/%.*$/, "comment"],

      // Begin/end environments
      [/(\\(?:begin|end))(\{)([a-zA-Z*]+)(\})/, ["keyword", "delimiter.curly", "tag", "delimiter.curly"]],

      // Section commands (special styling)
      [
        /\\(?:part|chapter|section|subsection|subsubsection|paragraph|subparagraph)\b/,
        "keyword.section",
      ],

      // Document structure commands
      [
        /\\(?:documentclass|usepackage|input|include|bibliography|bibliographystyle)\b/,
        "keyword.control",
      ],

      // Title-related commands
      [
        /\\(?:title|author|date|maketitle|thanks|institute)\b/,
        "keyword.control",
      ],

      // Common LaTeX commands
      [/\\(?:textbf|textit|texttt|emph|underline|textsc|textrm|textsf)\b/, "keyword.formatting"],

      // Math commands
      [/\\(?:frac|sqrt|sum|prod|int|lim|infty|partial|nabla|alpha|beta|gamma|delta|epsilon|zeta|eta|theta|iota|kappa|lambda|mu|nu|xi|pi|rho|sigma|tau|upsilon|phi|chi|psi|omega|sin|cos|tan|log|ln|exp|min|max|sup|inf|det|dim|ker|hom|arg|deg|gcd)\b/, "keyword.math"],

      // Generic commands (\commandname)
      [/\\[a-zA-Z@]+\*?/, "keyword"],

      // Special characters
      [/\\[\\{}$&#%_^~]/, "keyword.escape"],

      // Curly braces
      [/\{/, "delimiter.curly"],
      [/\}/, "delimiter.curly"],

      // Square brackets
      [/\[/, "delimiter.square"],
      [/\]/, "delimiter.square"],

      // Math mode (inline)
      [/\$\$/, { token: "string.math", next: "@mathDisplay" }],
      [/\$/, { token: "string.math", next: "@mathInline" }],

      // Numbers
      [/\d+(\.\d+)?/, "number"],
    ],

    mathInline: [
      [/[^$\\]+/, "string.math"],
      [/\\[a-zA-Z]+/, "keyword.math"],
      [/\\[\\{}$]/, "keyword.escape"],
      [/\$/, { token: "string.math", next: "@pop" }],
    ],

    mathDisplay: [
      [/[^$\\]+/, "string.math"],
      [/\\[a-zA-Z]+/, "keyword.math"],
      [/\\[\\{}$]/, "keyword.escape"],
      [/\$\$/, { token: "string.math", next: "@pop" }],
    ],
  },
}

/* ── Custom Light Theme (matching Figma design) ── */
export const latexLightTheme: editor.IStandaloneThemeData = {
  base: "vs",
  inherit: true,
  rules: [
    // Comments — muted gray
    { token: "comment", foreground: "6A9955", fontStyle: "italic" },

    // LaTeX commands — dark/black
    { token: "keyword", foreground: "1a1a1a", fontStyle: "bold" },
    { token: "keyword.section", foreground: "1a1a1a", fontStyle: "bold" },
    { token: "keyword.control", foreground: "1a1a1a", fontStyle: "bold" },
    { token: "keyword.formatting", foreground: "1a1a1a", fontStyle: "bold" },
    { token: "keyword.math", foreground: "1a1a1a", fontStyle: "bold" },
    { token: "keyword.escape", foreground: "1a1a1a" },

    // Environment names (inside \begin{...})
    { token: "tag", foreground: "0070C1" },

    // Regular text — dark gray
    { token: "text", foreground: "6b7280" },
    { token: "", foreground: "6b7280" },

    // Math mode
    { token: "string.math", foreground: "1a1a1a" },

    // Braces / brackets
    { token: "delimiter.curly", foreground: "1a1a1a" },
    { token: "delimiter.square", foreground: "1a1a1a" },

    // Numbers
    { token: "number", foreground: "098658" },
  ],
  colors: {
    "editor.background": "#FFFFFF",
    "editor.foreground": "#6b7280",
    "editor.lineHighlightBackground": "#f8f9fa",
    "editor.selectionBackground": "#b3d7ff",
    "editorLineNumber.foreground": "#9ca3af",
    "editorLineNumber.activeForeground": "#6b7280",
    "editorGutter.background": "#f9fafb",
    "editorCursor.foreground": "#1a1a1a",
    "editor.selectionHighlightBackground": "#dbe9ff",
    "editorIndentGuide.background": "#e5e7eb",
    "editorBracketMatch.background": "#e8e8e8",
    "editorBracketMatch.border": "#b0b0b0",
  },
}

/* ── Custom Dark Theme ── */
export const latexDarkTheme: editor.IStandaloneThemeData = {
  base: "vs-dark",
  inherit: true,
  rules: [
    { token: "comment", foreground: "6A9955", fontStyle: "italic" },
    { token: "keyword", foreground: "dcdcaa", fontStyle: "bold" },
    { token: "keyword.section", foreground: "dcdcaa", fontStyle: "bold" },
    { token: "keyword.control", foreground: "c586c0" },
    { token: "keyword.formatting", foreground: "569cd6" },
    { token: "keyword.math", foreground: "4ec9b0" },
    { token: "keyword.escape", foreground: "d7ba7d" },
    { token: "tag", foreground: "4ec9b0" },
    { token: "text", foreground: "d4d4d4" },
    { token: "", foreground: "d4d4d4" },
    { token: "string.math", foreground: "ce9178" },
    { token: "delimiter.curly", foreground: "ffd700" },
    { token: "delimiter.square", foreground: "ffd700" },
    { token: "number", foreground: "b5cea8" },
  ],
  colors: {
    "editor.background": "#1e1e1e",
    "editor.foreground": "#d4d4d4",
    "editorLineNumber.foreground": "#858585",
    "editorGutter.background": "#1e1e1e",
  },
}

export const latexGrayTheme: editor.IStandaloneThemeData = {
  base: "vs-dark",
  inherit: true,
  rules: latexDarkTheme.rules,
  colors: {
    "editor.background": "#262626",
    "editor.foreground": "#d4d4d4",
    "editorLineNumber.foreground": "#858585",
    "editorGutter.background": "#262626",
  },
}

/**
 * Register the LaTeX language with a Monaco instance.
 * Call this once before mounting any editors.
 */
export function registerLatexLanguage(monaco: typeof import("monaco-editor")) {
  // Only register if not already registered
  const langs = monaco.languages.getLanguages()
  if (langs.some((l) => l.id === LATEX_LANGUAGE_ID)) return

  monaco.languages.register({ id: LATEX_LANGUAGE_ID, extensions: [".tex", ".sty", ".cls", ".bib"] })
  monaco.languages.setLanguageConfiguration(LATEX_LANGUAGE_ID, latexLanguageConfiguration)
  monaco.languages.setMonarchTokensProvider(LATEX_LANGUAGE_ID, latexMonarchLanguage)

  // Register themes
  monaco.editor.defineTheme("latex-light", latexLightTheme)
  monaco.editor.defineTheme("latex-dark", latexDarkTheme)
  monaco.editor.defineTheme("latex-gray", latexGrayTheme)

  // Register intelligent completion provider
  registerLatexCompletionProvider(monaco)

  // Register AST-based folding provider
  registerLatexFoldingProvider(monaco)
}
